import { useState, useEffect, FormEvent } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Lock, Eye, EyeOff, ArrowLeft, CheckCircle2,
  XCircle, Loader2, AlertCircle,
} from 'lucide-react'
import { authApi } from '@/api/auth'
import { getErrorMessage } from '@/lib/api'
import { BRAND } from '@/config/brand'

export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [validationError, setValidationError] = useState('')

  // Verify token on page load
  const { data: tokenStatus, isLoading: verifyingToken } = useQuery({
    queryKey: ['verifyResetToken', token],
    queryFn: () => authApi.verifyResetToken(token),
    enabled: !!token,
    retry: false,
  })

  const mutation = useMutation({
    mutationFn: () =>
      authApi.resetPassword({ token, new_password: newPassword }),
    onSuccess: (data) => {
      toast.success(data.message || t('resetPassword.success'))
      setTimeout(() => navigate('/login'), 1500)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  // Password strength
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { label: '', color: 'bg-gray-200', width: '0%' }
    let score = 0
    if (pwd.length >= 8) score++
    if (pwd.length >= 12) score++
    if (/[A-Z]/.test(pwd)) score++
    if (/[a-z]/.test(pwd)) score++
    if (/[0-9]/.test(pwd)) score++
    if (/[^A-Za-z0-9]/.test(pwd)) score++

    if (score <= 2) return { label: t('profile.passwordWeak'), color: 'bg-red-500', width: '33%' }
    if (score <= 4) return { label: t('profile.passwordMedium'), color: 'bg-yellow-500', width: '66%' }
    return { label: t('profile.passwordStrong'), color: 'bg-green-500', width: '100%' }
  }

  const strength = getPasswordStrength(newPassword)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setValidationError('')

    if (newPassword.length < 8) {
      setValidationError(t('profile.passwordTooShort'))
      return
    }

    if (newPassword !== confirmPassword) {
      setValidationError(t('profile.passwordMismatch'))
      return
    }

    mutation.mutate()
  }

  // No token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('resetPassword.noToken')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('resetPassword.noTokenDesc')}
          </p>
          <Link to="/forgot-password" className="btn-primary inline-block">
            {t('resetPassword.requestNew')}
          </Link>
        </div>
      </div>
    )
  }

  // Verifying token
  if (verifyingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    )
  }

  // Invalid token
  if (tokenStatus && !tokenStatus.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('resetPassword.invalidToken')}
          </h2>
          <p className="text-gray-600 mb-6">
            {tokenStatus.message || t('resetPassword.invalidTokenDesc')}
          </p>
          <Link to="/forgot-password" className="btn-primary inline-block">
            {t('resetPassword.requestNew')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-8 text-center">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg bg-gradient-to-br ${BRAND.gradient}`}>
              <span className="text-white text-4xl font-bold">{BRAND.initial}</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {isArabic ? BRAND.nameAr : BRAND.name}
            </h1>
          </div>

          {/* Body */}
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('resetPassword.title')}
              </h2>
              {tokenStatus?.email ? (
                <p className="text-gray-600 text-sm">
                  {t('resetPassword.forAccount')} <strong dir="ltr">{tokenStatus.email}</strong>
                </p>
              ) : null}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.newPassword')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input pr-10"
                    dir="ltr"
                    autoComplete="new-password"
                    minLength={8}
                    required
                    disabled={mutation.isPending}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Strength meter */}
                {newPassword ? (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">{t('profile.passwordStrength')}</span>
                      <span className="text-xs font-medium text-gray-700">{strength.label}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`${strength.color} h-1.5 rounded-full transition-all`}
                        style={{ width: strength.width }}
                      />
                    </div>
                  </div>
                ) : null}

                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.passwordRequirements')}
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.confirmNewPassword')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pr-10"
                    dir="ltr"
                    autoComplete="new-password"
                    required
                    disabled={mutation.isPending}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword ? (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {t('profile.passwordMismatch')}
                  </p>
                ) : null}
              </div>

              {/* Validation Error */}
              {validationError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{validationError}</p>
                </div>
              ) : null}

              {/* Submit */}
              <button
                type="submit"
                disabled={
                  mutation.isPending ||
                  newPassword.length < 8 ||
                  newPassword !== confirmPassword
                }
                className="w-full btn-primary flex items-center justify-center gap-2 py-3"
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('resetPassword.resetting')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    {t('resetPassword.resetButton')}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-primary-600 inline-flex items-center gap-1"
              >
                <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
